// Demo backend: a seeded week kept in this browser only, for trying the app without an account.
import type {
  Backend, Checkin, ConsentKind, Profile, Session, Sharing, TrainerLink, TrainerNote, Upcoming,
} from './types.ts';

interface State {
  signedIn: boolean;
  email: string | null;
  profile: Profile;
  consents: ConsentKind[];
  sessions: Session[];
  checkins: Checkin[];
  upcoming: Upcoming[];
  notes: TrainerNote[];
  links: TrainerLink[];
}

const KEY = 'recover.demo.v1';
const hours = (n: number, from = Date.now()) => new Date(from + n * 3_600_000).toISOString();

function seed(): State {
  const now = Date.now();
  return {
    signedIn: false,
    email: null,
    profile: { id: 'demo', full_name: '', avatar: 'male', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, onboarded_at: null },
    consents: [],
    sessions: [
      { id: crypto.randomUUID(), kind: 'trainer', activity_type: 'strength', source: 'manual', status: 'logged', started_at: hours(-50, now),
        ended_at: hours(-49, now), intensity: 4, recovery: 3, note: null,
        muscles: ['chest.pec_upper', 'chest.pec_mid', 'chest.pec_lower', 'triceps.long_head', 'triceps.lateral_head', 'triceps.medial_head']
          .map((muscle_id) => ({ muscle_id, selection: 'group' as const })) },
      { id: crypto.randomUUID(), kind: 'solo', activity_type: 'running', source: 'manual', status: 'logged', started_at: hours(-20, now),
        ended_at: hours(-19.3, now), intensity: 3, recovery: 4, note: null,
        muscles: ['quads.rectus_femoris', 'quads.vastus_lateralis', 'calves.soleus', 'calves.gastroc_medial', 'glutes.glute_max']
          .map((muscle_id) => ({ muscle_id, selection: 'specific' as const })) },
    ],
    checkins: [],
    upcoming: [],
    notes: [
      { id: crypto.randomUUID(), body: 'Left knee was sensitive on lunges. Keep squats light next session.', muscle_ids: ['quads.vastus_medialis'],
        raise_next_session: true, raised_at: null, created_at: hours(-26, now), trainer_name: 'Yoni (demo trainer)' },
    ],
    links: [{ id: 'demo-link', trainer_name: 'Yoni (demo trainer)', share_muscles: true, share_ratings: true, share_solo: true, share_health: false }],
  };
}

export class DemoBackend implements Backend {
  readonly isDemo = true;
  private state: State;
  private listeners = new Set<() => void>();

  constructor(reset = false) {
    let saved: State | null = null;
    try { saved = reset ? null : JSON.parse(localStorage.getItem(KEY) ?? 'null'); } catch { /* private mode */ }
    this.state = saved ?? seed();
    this.save();
  }

  private save() {
    try { localStorage.setItem(KEY, JSON.stringify(this.state)); } catch { /* private mode */ }
  }

  private change<T>(fn: (s: State) => T): Promise<T> {
    const out = fn(this.state);
    this.save();
    return Promise.resolve(out);
  }

  userId() { return Promise.resolve(this.state.signedIn ? 'demo' : null); }
  email() { return Promise.resolve(this.state.signedIn ? this.state.email : null); }
  onAuthChange(cb: () => void) { this.listeners.add(cb); return () => { this.listeners.delete(cb); }; }
  sendCode() { return Promise.resolve(); }

  async verifyCode(email: string, code: string) {
    if (!/^\d{6,8}$/.test(code)) throw new Error('Enter the code from the email (any 6 to 8 digits in demo mode).');
    await this.change((s) => { s.signedIn = true; s.email = email; });
    this.listeners.forEach((l) => l());
  }

  async signOut() {
    this.state = seed();
    this.save();
    this.listeners.forEach((l) => l());
  }

  profile() { return Promise.resolve(this.state.signedIn ? { ...this.state.profile } : null); }
  updateProfile(p: Partial<Omit<Profile, 'id'>>) { return this.change((s) => { Object.assign(s.profile, p); }); }
  consents() { return Promise.resolve(new Set(this.state.consents)); }
  grantConsent(kind: ConsentKind) { return this.change((s) => { if (!s.consents.includes(kind)) s.consents.push(kind); }); }
  revokeConsent(kind: ConsentKind) { return this.change((s) => { s.consents = s.consents.filter((k) => k !== kind); }); }

  sessions(since: Date) {
    return Promise.resolve(this.state.sessions.filter((x) => new Date(x.started_at) >= since)
      .sort((a, b) => b.started_at.localeCompare(a.started_at)).map((x) => structuredClone(x)));
  }
  logSession(session: Session) {
    return this.change((s) => { s.sessions = [...s.sessions.filter((x) => x.id !== session.id), structuredClone(session)]; });
  }
  checkins(sinceDay: string) { return Promise.resolve(this.state.checkins.filter((c) => c.day >= sinceDay)); }
  saveCheckin(c: Checkin) { return this.change((s) => { s.checkins = [c, ...s.checkins.filter((x) => x.day !== c.day)]; }); }
  upcoming() {
    const now = new Date().toISOString();
    return Promise.resolve(this.state.upcoming.filter((u) => u.starts_at > now).sort((a, b) => a.starts_at.localeCompare(b.starts_at)));
  }
  syncUpcoming(events: { calendar_event_id: string; title: string; starts_at: string }[]) {
    return this.change((s) => { s.upcoming = events.map((e) => ({ id: e.calendar_event_id, ...e })); });
  }
  notes() { return Promise.resolve([...this.state.notes]); }
  links() { return Promise.resolve(this.state.links.map((l) => ({ ...l }))); }
  updateSharing(linkId: string, sharing: Sharing) {
    return this.change((s) => { const l = s.links.find((x) => x.id === linkId); if (l) Object.assign(l, sharing); });
  }
  revokeLink(linkId: string) { return this.change((s) => { s.links = s.links.filter((l) => l.id !== linkId); }); }
  createInvite() { return Promise.resolve(String(Math.floor(100000 + Math.random() * 900000))); }
  async deleteAccount() { await this.signOut(); }
}
